import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2,
  Plus,
  Trash2,
  KeyRound,
  Copy,
  Check,
} from "lucide-react";
import {
  API,
  fmtDate,
} from "./shared";

export function AccessCodesTab({ authHeader }) {
  const [codes, setCodes] = useState([]);
  const [master, setMaster] = useState("");
  const [note, setNote] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/access-codes`, { headers: authHeader() });
      setCodes(res.data.codes || []);
      setMaster(res.data.master_code || "");
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    try {
      await axios.post(`${API}/admin/access-codes`, { note, client_email: clientEmail }, { headers: authHeader() });
      setNote("");
      setClientEmail("");
      await load();
    } catch {
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (c) => {
    try {
      await axios.delete(`${API}/admin/access-codes/${c.id}`, { headers: authHeader() });
      load();
    } catch {}
  };

  const copy = async (c) => {
    try {
      await navigator.clipboard.writeText(c.code);
      setCopied(c.id);
      setTimeout(() => setCopied(null), 1500);
    } catch {}
  };

  return (
    <div data-testid="access-codes-tab">
      <div className="mb-6 flex flex-col lg:flex-row lg:items-end gap-4 lg:justify-between">
        <div className="max-w-xl">
          <div className="font-serif text-2xl text-[#3F5043] mb-1">
            Client Access Codes
          </div>
          <p className="text-sm text-[#3F5043]/70 leading-relaxed">
            Generate a code after a client pays. They enter it once in the Client
            Portal to unlock Soarlife AI, community groups, and record uploads.
          </p>
        </div>
        <form onSubmit={create} className="flex flex-wrap gap-2">
          <input
            data-testid="code-note-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (e.g. client name)"
            className="w-52 bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]"
          />
          <input
            data-testid="code-client-email-input"
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            placeholder="Client email (locks code to them)"
            className="w-64 bg-white border border-[#BCB3A6]/50 rounded-2xl px-4 py-2.5 text-sm text-[#3F5043] placeholder-[#3F5043]/40 focus:outline-none focus:ring-2 focus:ring-[#899480]"
          />
          <button
            type="submit"
            data-testid="code-generate-btn"
            disabled={creating}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#3F5043] text-[#F8F7F3] text-xs uppercase tracking-[0.18em] font-semibold hover:bg-[#875F45] transition-colors disabled:opacity-60 whitespace-nowrap"
          >
            {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Generate code
          </button>
        </form>
        <p className="text-[11px] text-[#3F5043]/55 max-w-xl -mt-1">
          Tip: add the client's email to lock the code to their account — it can't be shared or reused by anyone else.
        </p>
      </div>

      {master && (
        <div
          data-testid="master-code-card"
          className="mb-6 bg-[#3F5043] text-[#F8F7F3] rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-3"
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#AE8F78] font-semibold mb-1">
              Your private master code
            </div>
            <div className="font-mono text-xl tracking-[0.12em]">{master}</div>
            <div className="text-[11px] text-[#F8F7F3]/60 mt-1">
              Reusable — give it only to clients after they pay. Never post it publicly.
            </div>
          </div>
          <button
            data-testid="master-code-copy"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(master);
                setCopied("master");
                setTimeout(() => setCopied(null), 1500);
              } catch {}
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F8F7F3]/10 text-xs hover:bg-[#F8F7F3]/20"
          >
            {copied === "master" ? <Check size={12} /> : <Copy size={12} />}
            {copied === "master" ? "Copied" : "Copy"}
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center text-[#899480] py-10">
          <Loader2 size={18} className="animate-spin inline" /> Loading…
        </div>
      ) : codes.length === 0 ? (
        <div className="bg-white border border-[#BCB3A6]/40 rounded-3xl p-10 text-center" data-testid="codes-empty">
          <KeyRound size={26} strokeWidth={1.3} className="mx-auto text-[#899480] mb-3" />
          <div className="font-serif text-xl text-[#3F5043]">No codes yet.</div>
          <p className="text-sm text-[#3F5043]/60 mt-1">
            Generate your first code when a client pays.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {codes.map((c) => (
            <div
              key={c.id}
              data-testid={`code-item-${c.id}`}
              className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#BCB3A6]/40 rounded-2xl px-5 py-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-lg tracking-[0.15em] text-[#3F5043]">
                    {c.code}
                  </span>
                  {!c.is_active ? (
                    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full bg-[#BCB3A6]/30 text-[#3F5043]/60">
                      Revoked
                    </span>
                  ) : c.used_by ? (
                    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full bg-[#875F45]/15 text-[#875F45]">
                      Used · {c.used_by}
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded-full bg-[#899480]/20 text-[#3F5043]">
                      Active
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-[#899480] mt-1">
                  {c.note ? `${c.note} · ` : ""}Created {fmtDate(c.created_at)}
                  {c.assigned_email ? ` · locked to ${c.assigned_email}` : ""}
                </div>
              </div>
              {c.is_active && !c.used_by && (
                <div className="flex items-center gap-1">
                  <button
                    data-testid={`code-copy-${c.id}`}
                    onClick={() => copy(c)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#EDEDEE] text-[#3F5043] text-xs hover:bg-[#BCB3A6]/40"
                  >
                    {copied === c.id ? <Check size={12} /> : <Copy size={12} />}
                    {copied === c.id ? "Copied" : "Copy"}
                  </button>
                  <button
                    data-testid={`code-revoke-${c.id}`}
                    onClick={() => revoke(c)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[#875F45] text-xs hover:bg-[#875F45]/10"
                  >
                    <Trash2 size={12} /> Revoke
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
